// ── Shared hex parsing + WCAG contrast math ─────────────────────────────
// Pure, dependency-free, unit-tested. Used by accentTint() (index.ts) for
// the selected-card tint and by the builder section-divider color, which
// must fall back to slate when a merchant's primary color is too pale to
// read (see the .qb-toggle-row white-on-accent bug this codebase already
// shipped — don't repeat that shape of mistake for the divider).

export interface RGB {
  r: number;
  g: number;
  b: number;
}

/** `#0f172a` / `0f172a` / `#fd0` → `{r,g,b}`. Returns null for anything
 *  that isn't 3- or 6-digit hex (named colors, rgb(), empty strings). */
export function parseHexColor(input: string): RGB | null {
  if (typeof input !== "string") return null;
  let hex = input.trim().replace(/^#/, "");
  if (hex.length === 3) hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  if (!/^[0-9a-fA-F]{6}$/.test(hex)) return null;
  const n = parseInt(hex, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function channelLuminance(c: number): number {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
}

/** WCAG relative luminance, 0 (black) – 1 (white). */
export function relativeLuminance({ r, g, b }: RGB): number {
  return 0.2126 * channelLuminance(r) + 0.7152 * channelLuminance(g) + 0.0722 * channelLuminance(b);
}

/** WCAG contrast ratio between two colors, 1 (identical) – 21 (black/white). */
export function contrastRatio(a: RGB, b: RGB): number {
  const l1 = relativeLuminance(a);
  const l2 = relativeLuminance(b);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

/** Picks `primaryColor` for a graphical element (e.g. a divider) drawn on
 *  `backdropHex`, provided it clears `minContrast` (WCAG 1.4.11 non-text
 *  contrast is 3:1). Falls back to `fallbackColor` when the primary is too
 *  pale to read, or isn't parseable hex at all. */
export function resolveDividerColor(
  primaryColor: string,
  backdropHex: string,
  fallbackColor: string,
  minContrast = 3,
): string {
  const primaryRgb = parseHexColor(primaryColor);
  if (!primaryRgb) return fallbackColor;
  const backdropRgb = parseHexColor(backdropHex);
  if (!backdropRgb) return fallbackColor;
  return contrastRatio(primaryRgb, backdropRgb) >= minContrast ? primaryColor : fallbackColor;
}
